import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import api from '../../lib/api';

export const MinutesAdmin = () => {
  const [minutes, setMinutes] = useState([]);
  const [form, setForm] = useState({ title: '', meeting_date: '', content: '' });
  const [saving, setSaving] = useState(false);
  const [agenda, setAgenda] = useState('');
  const [agendaSaving, setAgendaSaving] = useState(false);

  useEffect(() => {
    api.get('/next-meeting-agenda').then((res) => setAgenda(res.data.agenda || '')).catch(() => {});
  }, []);

  const saveAgenda = async () => {
    setAgendaSaving(true);
    try {
      const res = await api.put('/admin/next-meeting-agenda', { text: agenda });
      toast.success(res.data.message);
    } catch {
      toast.error('Could not save the agenda.');
    } finally {
      setAgendaSaving(false);
    }
  };

  const load = useCallback(() => {
    api.get('/minutes').then((res) => setMinutes(res.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/admin/minutes', form);
      toast.success('Meeting minutes published to members.');
      setForm({ title: '', meeting_date: '', content: '' });
      load();
    } catch {
      toast.error('Could not save minutes — please try again.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/minutes/${id}`);
      toast.success('Minutes deleted.');
      load();
    } catch {
      toast.error('Delete failed.');
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-serif text-xl text-[#3c4a2a] mb-1">Meeting Minutes</h2>
        <p className="text-sm text-[#3c4a2a]/60">Published minutes are visible to logged-in members only.</p>
      </div>

      <div className="bg-white border border-[#bd8cae]/30 rounded-xl p-6 space-y-3 max-w-3xl shadow-sm" data-testid="agenda-editor">
        <div>
          <h3 className="font-serif text-lg text-[#3c4a2a]">Next Meeting Agenda</h3>
          <p className="text-sm text-[#3c4a2a]/60">Shown to members on their dashboard until you change it. Clear the text to hide it.</p>
        </div>
        <Textarea
          rows={5}
          placeholder={"1. Welcome & introductions\n2. Committee updates\n3. New business…"}
          value={agenda}
          onChange={(e) => setAgenda(e.target.value)}
          data-testid="agenda-input"
        />
        <Button onClick={saveAgenda} disabled={agendaSaving} data-testid="agenda-save-btn" className="bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white">
          {agendaSaving ? 'Saving…' : 'Save Agenda'}
        </Button>
      </div>

      <form onSubmit={submit} className="bg-[#f1e3eb]/60 rounded-xl p-6 space-y-4 max-w-3xl" data-testid="minutes-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-[#3c4a2a]">Meeting Date *</Label>
            <Input
              type="date"
              required
              value={form.meeting_date}
              onChange={(e) => setForm({ ...form, meeting_date: e.target.value })}
              data-testid="minutes-date-input"
              className="mt-1 bg-white"
            />
          </div>
          <div>
            <Label className="text-[#3c4a2a]">Title *</Label>
            <Input
              required
              placeholder="e.g., October Membership Meeting"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="minutes-title-input"
              className="mt-1 bg-white"
            />
          </div>
        </div>
        <div>
          <Label className="text-[#3c4a2a]">Minutes *</Label>
          <Textarea
            required
            rows={8}
            placeholder="Attendance, motions, decisions, next steps…"
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            data-testid="minutes-content-input"
            className="mt-1 bg-white"
          />
        </div>
        <Button type="submit" disabled={saving} data-testid="minutes-submit-btn" className="bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white">
          <Plus className="w-4 h-4 mr-2" />
          {saving ? 'Publishing…' : 'Publish Minutes'}
        </Button>
      </form>

      <div className="space-y-3 max-w-3xl" data-testid="minutes-admin-list">
        {minutes.length === 0 ? (
          <p className="text-sm text-[#3c4a2a]/50">No minutes published yet.</p>
        ) : (
          minutes.map((m) => (
            <div key={m.id} className="flex items-start justify-between bg-white border border-[#bd8cae]/20 rounded-lg p-4 shadow-sm">
              <div className="min-w-0">
                <p className="font-medium text-[#3c4a2a]">{m.title}</p>
                <p className="text-xs text-[#3c4a2a]/50">{m.meeting_date}</p>
                <p className="text-sm text-[#3c4a2a]/70 mt-1 line-clamp-2 whitespace-pre-wrap">{m.content}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => remove(m.id)}
                data-testid={`minutes-delete-${m.id}`}
                className="text-red-500 hover:text-red-700 flex-shrink-0 ml-3"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
